import type { TimeEntry, TimeEntryStatus } from "../types";
import { entryMinutes } from "./format";

export interface DayGroup {
	key: string;
	workDate: string;
	entries: TimeEntry[];
}

function byClockedIn(a: TimeEntry, b: TimeEntry): number {
	return a.clockedInAt.localeCompare(b.clockedInAt);
}

export function sortIntervals(entries: TimeEntry[]): TimeEntry[] {
	return [...entries].sort(byClockedIn);
}

export function groupByKey(
	entries: TimeEntry[],
	keyOf: (entry: TimeEntry) => string,
): DayGroup[] {
	const groups = new Map<string, DayGroup>();
	for (const entry of entries) {
		const key = keyOf(entry);
		const group = groups.get(key);
		if (group) {
			group.entries.push(entry);
		} else {
			groups.set(key, { key, workDate: entry.workDate, entries: [entry] });
		}
	}
	for (const group of groups.values()) {
		group.entries.sort(byClockedIn);
	}
	return [...groups.values()];
}

export function groupByWorkDate(
	entries: TimeEntry[],
): Map<string, TimeEntry[]> {
	return new Map(
		groupByKey(entries, (entry) => entry.workDate).map((group) => [
			group.workDate,
			group.entries,
		]),
	);
}

export function groupByWorkerAndWorkDate(entries: TimeEntry[]): DayGroup[] {
	return groupByKey(entries, (entry) => `${entry.worker.id}:${entry.workDate}`);
}

export function latestEntryByWorker(
	entries: TimeEntry[],
): Map<string, TimeEntry> {
	const latest = new Map<string, TimeEntry>();
	for (const group of groupByKey(entries, (entry) => entry.worker.id)) {
		const entry = latestEntry(group.entries);
		if (entry) latest.set(group.key, entry);
	}
	return latest;
}

export function countPresentWorkers(entries: TimeEntry[]): number {
	let present = 0;
	for (const entry of latestEntryByWorker(entries).values()) {
		if (!entry.clockedOutAt) present += 1;
	}
	return present;
}

export function entriesForDate(
	entries: TimeEntry[],
	workDate: string,
): TimeEntry[] {
	return sortIntervals(entries.filter((entry) => entry.workDate === workDate));
}

export function hasOpenEntry(entries: TimeEntry[]): boolean {
	return entries.some((entry) => !entry.clockedOutAt);
}

export function openEntry(entries: TimeEntry[]): TimeEntry | undefined {
	return entries.find((entry) => !entry.clockedOutAt);
}

export function latestEntry(entries: TimeEntry[]): TimeEntry | undefined {
	return openEntry(entries) ?? sortIntervals(entries).at(-1);
}

export function dayStatus(entries: TimeEntry[]): TimeEntryStatus {
	if (entries.length === 0) return "missing";
	return hasOpenEntry(entries) ? "open" : "closed";
}

export function sumMinutes(entries: TimeEntry[], now: Date): number {
	return entries.reduce((total, entry) => total + entryMinutes(entry, now), 0);
}

export function countClosedDays(entries: TimeEntry[]): number {
	let closed = 0;
	for (const group of groupByWorkDate(entries).values()) {
		if (dayStatus(group) === "closed") closed += 1;
	}
	return closed;
}
